import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= User Interaction Types =============
/**
 * Types of user interactions that can be detected in frontend code.
 */
export const UserInteractionType = z.enum([
    'click', // Button clicks, element clicks
    'submit', // Form submissions
    'navigate', // Route navigation
    'fetch', // Data fetching (useEffect, React Query, SWR)
    'realtime', // WebSocket, SSE, real-time events
    'file', // File uploads, downloads
    'gesture', // Touch gestures, scroll events
]);
/**
 * Handler type classification for event handlers.
 */
export const HandlerType = z.enum([
    'reference', // Direct function reference: onClick={handleClick}
    'inline', // Inline arrow function: onClick={() => doSomething()}
    'bound', // Bound method: onClick={this.handleClick.bind(this)}
]);
// ============= Click Handler =============
/**
 * Represents a detected click/submit event handler.
 */
export const ClickHandlerSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Type of DOM element (button, div, a, form, etc.) */
    elementType: z.string(),
    /** Text content of the element if available */
    elementText: z.string().optional(),
    /** Event type (onClick, onSubmit, onChange, etc.) */
    eventType: z.string(),
    /** How the handler is defined */
    handlerType: HandlerType,
    /** Name of the handler function */
    handlerName: z.string(),
    /** File where handler is defined */
    handlerFile: z.string(),
    /** Line number of handler definition */
    handlerLine: z.number(),
    /** Functions called within the handler */
    calledFunctions: z.array(z.string()),
    /** API endpoints called from this handler */
    apiEndpoints: z.array(z.string()).optional(),
    /** State setters called */
    stateSetters: z.array(z.string()).optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Form Submission =============
/**
 * Represents a detected form submission.
 */
export const FormSubmissionSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Form element identifier or name */
    formId: z.string().optional(),
    /** Form action URL if specified */
    actionUrl: z.string().optional(),
    /** HTTP method (GET, POST) */
    method: z.string().optional(),
    /** Handler function name */
    handlerName: z.string(),
    /** Handler file */
    handlerFile: z.string(),
    /** Handler line number */
    handlerLine: z.number(),
    /** Form field names detected */
    fields: z.array(z.string()).optional(),
    /** Validation library used (zod, yup, etc.) */
    validationLibrary: z.string().optional(),
    /** Functions called by the handler (#791) */
    calledFunctions: z.array(z.string()).optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Navigation =============
/**
 * Represents a detected navigation/routing action.
 */
export const NavigationSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Type of navigation */
    type: z.enum(['link', 'programmatic', 'redirect']),
    /** Destination route path */
    destination: z.string(),
    /** Whether destination is dynamic (contains variables) */
    isDynamic: z.boolean(),
    /** Component containing the navigation */
    sourceComponent: z.string(),
    /** File containing the navigation */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** What triggers the navigation (click, condition, etc.) */
    triggerContext: z.string().optional(),
    /** Whether navigation is conditional */
    isConditional: z.boolean(),
    /** Dynamic route parameters used */
    routeParams: z.array(z.string()).optional(),
    /** Router library detected */
    routerLibrary: z.enum(['react-router', 'next', 'vue-router', 'react-navigation', 'expo-router', 'unknown']).optional(),
    /** Text content of the Link element (Issue #433) */
    linkText: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Data Fetch =============
/**
 * Represents a detected data fetching operation.
 */
export const DataFetchSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Type of data fetch */
    type: z.enum(['query', 'mutation', 'effect', 'swr', 'apollo']),
    /** Library used */
    library: z.enum(['react-query', 'swr', 'apollo', 'native', 'axios', 'ky', 'rtk-query', 'unknown']),
    /** API endpoint if detectable */
    endpoint: z.string().optional(),
    /** HTTP method */
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']).optional(),
    /** Query key for caching libraries */
    queryKey: z.array(z.string()).optional(),
    /** Name of the fetcher function */
    fetcherFunction: z.string().optional(),
    /** Dependencies (useEffect deps, query keys) */
    dependencies: z.array(z.string()).optional(),
    /** Component or hook containing the fetch */
    sourceComponent: z.string(),
    /** File path */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Whether this is for mutation (write) or query (read) */
    isMutation: z.boolean(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Real-Time Event =============
/**
 * Represents a detected real-time event (WebSocket, SSE, etc.).
 */
export const RealTimeEventSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Type of real-time connection */
    type: z.enum(['websocket', 'socket.io', 'sse', 'pusher', 'ably', 'unknown']),
    /** Connection endpoint/URL */
    endpoint: z.string().optional(),
    /** Event or message name */
    eventName: z.string().optional(),
    /** Direction of data flow */
    direction: z.enum(['incoming', 'outgoing', 'bidirectional']),
    /** Handler function name */
    handlerName: z.string().optional(),
    /** Channel or room if applicable */
    channel: z.string().optional(),
    /** File path */
    sourceFile: z.string(),
    /** Line number */
    sourceLine: z.number(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Enhanced Interaction Data Types (Issues #345-353) =============
/**
 * Navigation target details for navigation interactions (#345)
 */
export const NavigationTargetDetailsSchema = z.object({
    /** Route path being navigated to */
    path: z.string(),
    /** Destination component name */
    component: z.string(),
    /** File containing the destination component */
    file: z.string().optional(),
    /** Whether the route has dynamic segments */
    isDynamic: z.boolean(),
    /** Route parameters if dynamic */
    params: z.record(z.string()).optional(),
});
/**
 * DOM effects caused by an interaction (#346)
 */
export const DomEffectsSchema = z.object({
    /** Elements that are conditionally rendered */
    conditionalRender: z.array(z.string()).optional(),
    /** CSS classes that are toggled */
    cssClassChanges: z.array(z.string()).optional(),
    /** Attributes that are modified */
    attributeChanges: z.array(z.string()).optional(),
    /** JSX elements affected by this interaction */
    affectedElements: z.array(z.string()).optional(),
});
/**
 * Affected element details for state interactions (#347)
 */
export const AffectedElementSchema = z.object({
    /** Element identifier (component name, CSS selector, etc.) */
    elementId: z.string(),
    /** Type of element (component, div, span, etc.) */
    elementType: z.string(),
    /** Type of change caused */
    changeType: z.enum(['visibility', 'content', 'style', 'attribute']),
    /** Human-readable description of the effect */
    description: z.string(),
});
/**
 * Clipboard content details for copy interactions (#350)
 */
export const ClipboardContentSchema = z.object({
    /** Type of content being copied */
    contentType: z.enum(['text', 'url', 'code', 'data']),
    /** Preview of the content (first 50 chars) */
    preview: z.string().optional(),
    /** How the user is notified (toast, tooltip, etc.) */
    confirmationUI: z.string().optional(),
});
/**
 * Form validation details for form submissions (#351)
 */
export const FormValidationDetailsSchema = z.object({
    /** Form field names */
    fields: z.array(z.string()),
    /** Validation rules detected */
    validationRules: z.array(z.string()).optional(),
    /** How errors are displayed */
    errorDisplay: z.enum(['inline', 'toast', 'summary', 'unknown']).optional(),
});
/**
 * API summary for API-bound interactions (#352)
 */
export const ApiSummarySchema = z.object({
    /** HTTP method */
    method: z.string(),
    /** API endpoint */
    endpoint: z.string(),
    /** Request body field names */
    requestBody: z.array(z.string()).optional(),
    /** Expected response fields */
    responseFields: z.array(z.string()).optional(),
    /** Error handling approach */
    errorHandling: z.string().optional(),
});
// ============= User Interaction (Combined) =============
/**
 * Unified representation of any user interaction detected in the codebase.
 */
export const UserInteractionSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Human-readable name for the interaction */
    name: z.string(),
    /** Type of interaction */
    type: UserInteractionType,
    /** Element that triggers the interaction (button, form, link, etc.) */
    triggerElement: z.string(),
    /** Text content of the trigger element (button label, link text, etc.) - Issue #378 */
    elementText: z.string().optional(),
    /** Handler function name */
    handlerFunction: z.string(),
    /** File containing the handler */
    handlerFile: z.string(),
    /** Line number of the handler */
    handlerLine: z.number(),
    /** API endpoints called by this interaction */
    associatedApiCalls: z.array(z.string()),
    /** State changes triggered */
    stateChanges: z.array(z.string()),
    /** Component where interaction is defined */
    component: z.string().optional(),
    /** Whether the interaction triggers navigation */
    triggersNavigation: z.boolean().optional(),
    /** Navigation destination if applicable (legacy string field) */
    navigationTarget: z.string().optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
    // ============= Issue #452: Fields for category detection =============
    /** Functions called by this handler (for category detection) */
    calledFunctions: z.array(z.string()).optional(),
    /** Event type that triggers this interaction (onClick, onKeyDown, etc.) */
    eventType: z.string().optional(),
    // ============= Enhanced Fields (Issues #345-353) =============
    /** Detailed navigation target info (#345) */
    navigationTargetDetails: NavigationTargetDetailsSchema.optional(),
    /** DOM effects caused by the interaction (#346) */
    domEffects: DomEffectsSchema.optional(),
    /** Elements affected by state changes (#347) */
    affectedElements: z.array(AffectedElementSchema).optional(),
    /** Clipboard content details (#350) */
    clipboardContent: ClipboardContentSchema.optional(),
    /** Form validation details (#351) */
    formValidation: FormValidationDetailsSchema.optional(),
    /** API call summary (#352) */
    apiSummary: ApiSummarySchema.optional(),
    // ============= Prop Callback Resolution Fields (Issue #411) =============
    /** Whether this handler is a callback prop passed from a parent component */
    isPropCallback: z.boolean().optional(),
    /** The resolved handler name in the parent component */
    resolvedHandler: z.string().optional(),
    /** File where the resolved handler is defined */
    resolvedHandlerFile: z.string().optional(),
    /** Line where the resolved handler is defined */
    resolvedHandlerLine: z.number().optional(),
    /** State setters called by the resolved handler (in parent component) */
    resolvedStateSetters: z.array(z.string()).optional(),
    /** Parent component that passes this callback prop */
    parentComponent: z.string().optional(),
    // ============= Stitch Method Tracking (Issue #745) =============
    /** How this interaction was linked to its API calls */
    stitchMethod: z.enum(['call-graph', 'import-graph', 'path-match', 'unresolved']).optional(),
    /** Number of call graph hops to reach the API call (call-graph stitch only) */
    stitchHops: z.number().optional(),
    // ============= Direct DB Query Linking (Issue #784) =============
    /** DB query IDs linked directly to this interaction (BaaS/Supabase inline queries) */
    associatedDbQueries: z.array(z.string()).optional(),
    // ============= Trigger metadata (#1060-12B) =============
    /**
     * Framework-specific trigger metadata attached at projection time. Currently
     * captures Vue's event modifier list (`@click.stop.prevent` → `modifiers:
     * ['stop','prevent']`); React/Angular triggers leave this `undefined`.
     *
     * Surfaces in catalog UI / diagram edge labeler as small badges next to the
     * trigger row so reviewers can tell at a glance whether the handler is
     * gated by `event.stopPropagation` / `event.preventDefault` / a key
     * constraint — semantic info that materially affects flow behaviour.
     */
    triggerMetadata: z.object({
        /** Vue: `['stop', 'prevent', 'enter', 'exact', …]` (order preserved). */
        modifiers: z.array(z.string()).optional(),
    }).optional(),
});
// ============= Detection Result =============
/**
 * Complete result from user interaction detection.
 */
export const UserInteractionDetectionResultSchema = z.object({
    /** All detected click handlers */
    clickHandlers: z.array(ClickHandlerSchema),
    /** All detected form submissions */
    formSubmissions: z.array(FormSubmissionSchema),
    /** All detected navigations */
    navigations: z.array(NavigationSchema),
    /** All detected data fetches */
    dataFetches: z.array(DataFetchSchema),
    /** All detected real-time events */
    realTimeEvents: z.array(RealTimeEventSchema),
    /** Unified list of all interactions */
    interactions: z.array(UserInteractionSchema),
    /** Summary statistics */
    summary: z.object({
        totalInteractions: z.number(),
        byType: z.record(z.number()),
        filesAnalyzed: z.number(),
        componentsWithInteractions: z.number(),
    }),
});
// ============= API Round-Trip Types (Issue #369) =============
/**
 * Response handling type - how the frontend handles API response
 */
export const ResponseHandlingType = z.enum([
    'then', // .then() chain
    'await', // async/await
    'callback', // callback function
    'mutation-hook', // React Query/SWR mutation hooks (onSuccess, onError)
    'event-listener', // XHR/WebSocket/EventSource event listeners (onmessage, onload, etc.)
]);
/**
 * Information about how an API response is handled (#369)
 */
export const ResponseHandlingSchema = z.object({
    /** How the response is handled */
    type: ResponseHandlingType,
    /** Name of the callback/handler function */
    handlerName: z.string().optional(),
    /** Line where response is processed */
    handlerLine: z.number().optional(),
    /** State setters called in the callback */
    stateUpdates: z.array(z.string()).optional(),
    /** Navigation triggered after response */
    navigationTarget: z.string().optional(),
    /** Toast/notification calls */
    notificationCall: z.string().optional(),
    /** Query invalidation calls (React Query) */
    queryInvalidations: z.array(z.string()).optional(),
    /** Whether error handling is present */
    hasErrorHandling: z.boolean().optional(),
    /** Error handler name if present */
    errorHandlerName: z.string().optional(),
});
/**
 * API response information for flow visualization (#369)
 */
export const ApiResponseInfoSchema = z.object({
    /** Response type description (e.g., "Created resource", "Array of items") */
    type: z.string(),
    /** HTTP status code */
    statusCode: z.number(),
    /** Content type */
    contentType: z.string().optional(),
    /** Response fields if known */
    fields: z.array(z.string()).optional(),
});
/**
 * API callback information for flow visualization (#369)
 */
export const ApiCallbackInfoSchema = z.object({
    /** Callback type (then, await, onSuccess, etc.) */
    type: ResponseHandlingType,
    /** State variables updated */
    stateUpdates: z.array(z.string()).optional(),
    /** Navigation target if callback triggers navigation */
    navigationTarget: z.string().optional(),
    /** Notification/toast shown */
    notificationCall: z.string().optional(),
    /** Cache invalidations triggered */
    cacheInvalidations: z.array(z.string()).optional(),
    /** Whether callback refetches data */
    refetchesData: z.boolean().optional(),
});
/**
 * DOM update information for flow visualization (#369)
 */
export const DomUpdateInfoSchema = z.object({
    /** Update type */
    updateType: z.enum(['visibility', 'content', 'style', 'remove', 'add', 'navigate', 'notification']),
    /** User-friendly description */
    description: z.string(),
    /** Technical detail for developers */
    technicalDetail: z.string().optional(),
    /** Affected elements */
    affectedElements: z.array(z.string()).optional(),
});
/**
 * Function call phase classification (#369)
 * Classifies functions called in a handler by when they execute relative to API call
 */
export const FunctionPhasesSchema = z.object({
    /** Functions called before API (validation, loading state) */
    preApiCalls: z.array(z.string()),
    /** The API call itself */
    apiCall: z.string().optional(),
    /** Functions called after API response (success handlers, navigation) */
    postApiCalls: z.array(z.string()),
});
//# sourceMappingURL=userInteraction.js.map